import { router } from '@inertiajs/react'
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Pagination,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material'
import theme from './Theme';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Recommendation from '../components/Recommendation';
import { useState } from 'react';
import Layout from './Layout';

interface MedicationMatch {
  id: number;
  birthdate: string;
  age: number;
  race: string;
  weight: number;
  height: number;
  bmi: number;
  skip_period: boolean;
  has_acne: boolean;
  has_pcos: boolean;
  paying_cash: boolean;
  created_at: string;
  medication_name: string;
  medication_family: string;
  notes: string;
  recommendations: {
    medication_name: string;
    medication_family: string;
    notes: string[];
  }[];
}

interface Props {
  matches: MedicationMatch[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_count: number;
  };
}

function Row({ match }: { match: MedicationMatch }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <TableRow key={match.id} sx={{ backgroundColor: open ? theme.pandia.blue : 'white' }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {new Date(match.created_at).toLocaleDateString('en-US', { year: "2-digit", month: '2-digit', day: '2-digit' })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(match.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {match.age}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {match.birthdate && "DOB: " + new Date(match.birthdate).toLocaleDateString('en-US', { year: "2-digit", month: '2-digit', day: '2-digit' })}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            {match.bmi}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {Math.floor(match.height / 12) + "'" + match.height % 12 + "'' " + match.weight + "lbs"}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {match.recommendations[0].medication_name}
              {match.recommendations.length > 1 && <Chip 
                variant="outlined"
                size="small" 
                label="BEST"
                sx={{ borderRadius: '4px', bgcolor: theme.pandia.darker_green, color: 'white', height: '16px', fontSize: '10px', fontWeight: '500' }} 
              />}
            </Box>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {match.recommendations[1]?.medication_name}
          </Typography>
        </TableCell>
        <TableCell>{match.skip_period ? 'Skipping period' : 'Regular period'}</TableCell>
        <TableCell>{(match.race)}</TableCell>
        <TableCell>{match.has_acne ? 'Yes' : 'No'}</TableCell>
        <TableCell>{match.has_pcos ? 'Yes' : 'No'}</TableCell>
        <TableCell>{match.paying_cash ? 'Cash' : 'Insurance'}</TableCell>
      </TableRow>
      <TableRow sx={{ backgroundColor: open ? theme.pandia.blue : 'white' }}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <TableCell>
              <Box sx={{ width: '350px' }}>
                {match.recommendations.map((recommendation) => (
                  <Recommendation key={recommendation.medication_name}  shadow={false} recommendation={recommendation} />
                ))}
              </Box>
            </TableCell>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export default function History({ matches, pagination }: Props) {
  return (
    <Layout title="History">
      <Container sx={{ mt: 15, mb: 4 }}>
        <Typography variant="h4">Medication recommendation history</Typography>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>Last 10 medication matches</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ bgcolor: '#E8E9EA' }}>
              <TableRow>
                <TableCell></TableCell>
                <TableCell>Date & time</TableCell>
                <TableCell>Age</TableCell>
                <TableCell>BMI</TableCell>
                <TableCell>Medication</TableCell>
                <TableCell>Period preference</TableCell>
                <TableCell>Race</TableCell>
                <TableCell>Acne</TableCell>
                <TableCell>PCOS</TableCell>
                <TableCell>Payment preference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {matches.map((match) => (<Row key={match.id} match={match} />))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination 
            count={pagination.total_pages}
            page={pagination.current_page}
            onChange={(_, page) => {
              router.visit(`/history?page=${page}`);
            }}
          />
        </Box>
      </Container>
    </Layout>
  )
}
